import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
  useContext,
} from "react";
import { UserPreferences, MapPreferences } from "../types";
import { settingsService, Settings } from "../services/settingsService";
import { getCameraService } from "../services/cameraService";
import debugx from "debugx";

const debug = debugx("bikedash:preferences");

export interface PreferencesContextValue {
  userPreferences: UserPreferences;
  mapPreferences: MapPreferences;
  updateUserPreferences: (prefs: Partial<UserPreferences>) => void;
  updateMapPreferences: (prefs: Partial<MapPreferences>) => void;
  toggleStarredMap: (styleId: string) => void;
  setApiKey: (service: string, key: string) => void;
}

export const PreferencesContext = createContext<PreferencesContextValue | null>(
  null,
);

interface PreferencesProviderProps {
  children: ReactNode;
}

export function PreferencesProvider({ children }: PreferencesProviderProps) {
  const [settings, setSettings] = useState<Settings>(() => {
    const initial = settingsService.getSettings();
    return {
      user: { ...initial.user },
      map: { ...initial.map },
    };
  });

  useEffect(() => {
    // Subscribe to settings changes
    debug("PreferencesContext subscribing to settings changes");
    const unsubscribe = settingsService.subscribe((newSettings) => {
      // Create completely new objects to ensure React detects the change
      setSettings({
        user: { ...newSettings.user },
        map: { ...newSettings.map },
      });
    });

    // Don't load settings from server on startup - it's causing reset issues
    // settingsService.loadFromServer();

    // Listen for theme changes if auto
    // REMOVED - this was causing unnecessary updates

    return () => {
      debug("PreferencesContext unsubscribing from settings changes");
      unsubscribe();
    };
  }, []);

  const updateUserPreferences = useCallback(
    (prefs: Partial<UserPreferences>) => {
      debug("Context: updateUserPreferences called with:", prefs);
      settingsService.updateUserPreferences(prefs);

      // Sync camera settings with camera service
      const cameraService = getCameraService();
      if ("cameraEnabled" in prefs || "cameraType" in prefs) {
        const currentSettings = cameraService.getSettings();
        cameraService.updateSettings({
          enabled: prefs.cameraEnabled ?? currentSettings.enabled,
          type: prefs.cameraType ?? currentSettings.type,
        });
      }
    },
    [],
  );

  const updateMapPreferences = useCallback((prefs: Partial<MapPreferences>) => {
    debug("Context: updateMapPreferences called with:", prefs);
    settingsService.updateMapPreferences(prefs);
  }, []);

  const toggleStarredMap = useCallback((styleId: string) => {
    debug("Context: toggleStarredMap called with:", styleId);
    settingsService.toggleStarredMap(styleId);
  }, []);

  const setApiKey = useCallback((service: string, key: string) => {
    debug("Context: setApiKey called with:", service, key);
    settingsService.setApiKey(service, key);
  }, []);

  const value: PreferencesContextValue = useMemo(() => {
    return {
      userPreferences: settings.user,
      mapPreferences: settings.map,
      updateUserPreferences,
      updateMapPreferences,
      toggleStarredMap,
      setApiKey,
    };
  }, [
    settings.user,
    settings.map,
    updateUserPreferences,
    updateMapPreferences,
    toggleStarredMap,
    setApiKey,
  ]);

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences() {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error("usePreferences must be used within PreferencesProvider");
  }
  return context;
}
